/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { SuccessResponse } from '../housien-models/SuccessResponse';
import type { PendingInvitation } from '../housien-models/PendingInvitation';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';

/**
 * Invites a new member to an agency via `POST /agencies/{agencyId}/invitations`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The invited email, role, and optional message
 * @returns A cancelable promise resolving to the invitation success response
 */
/**
 * Invites a new member to an agency via `POST /agencies/{agencyId}/invitations`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The invited email, target role, and optional message
 * @returns A cancelable promise resolving to the invitation result
 */
export function inviteAgencyMember(
agencyId: string,
requestBody: {
invited_email?: string;
invited_role?: string;
message?: string;
},
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'POST',
        url: '/agencies/{agencyId}/invitations',
        path: {
            'agencyId': agencyId,
        },
        body: requestBody,
        mediaType: 'application/json',
    });
}

/**
 * Accepts a pending agency invitation via
 * `POST /user/invitations/{invitationId}/accept`.
 *
 * @param invitationId - The invitation identifier
 * @param token - The invitation acceptance token
 * @returns A cancelable promise resolving to the acceptance success response
 */
/**
 * Accepts a pending agency invitation via
 * `POST /user/invitations/{invitationId}/accept`.
 *
 * @param invitationId - The invitation identifier
 * @param token - Optional JWT from the invitation email deep-link
 * @returns A cancelable promise resolving to the acceptance result
 */
export function acceptAgencyInvite(
invitationId: string,
token?: string,
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'POST',
        url: '/user/invitations/{invitationId}/accept',
        path: {
            'invitationId': invitationId,
        },
        body: token ? { token } : {},
        mediaType: 'application/json',
    });
}

/**
 * Changes an existing member's role within an agency via
 * `PUT /agencies/{agencyId}/member/role`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The target member's email and the new role
 * @returns A cancelable promise resolving to the update success response
 */
/**
 * Changes an existing agency member's role via
 * `PUT /agencies/{agencyId}/member/role`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The target user email and the role to assign
 * @returns A cancelable promise resolving to the update result
 */
export function changeUserRole(
agencyId: string,
requestBody: {
user_email: string;
role: 'admin' | 'broker' | 'operational' | 'anonymus';
},
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'PUT',
        url: '/agencies/{agencyId}/member/role',
        path: {
            'agencyId': agencyId,
        },
        body: requestBody,
        mediaType: 'application/json',
    });
}

/**
 * Removes a member from an agency via `DELETE /agencies/{agencyId}/member`,
 * identifying the member by email query parameter.
 *
 * @param agencyId - The agency identifier
 * @param userEmail - The email of the member to remove
 * @returns A cancelable promise resolving to the removal success response
 */
/**
 * Removes a member from an agency via `DELETE /agencies/{agencyId}/member`.
 *
 * @param agencyId - The agency identifier
 * @param userEmail - The email of the member to remove
 * @returns A cancelable promise resolving to the removal result
 */
export function removeMemberFromAgency(
agencyId: string,
userEmail: string,
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'DELETE',
        url: '/agencies/{agencyId}/member',
        path: {
            'agencyId': agencyId,
        },
        query: {
            'user_email': userEmail,
        },
    });
}

/**
 * Lists the current user's pending agency invitations via
 * `GET /user/invitations`.
 *
 * @returns A cancelable promise resolving to the array of pending invitations
 */
/**
 * Lists the current user's pending agency invitations via
 * `GET /user/invitations`.
 *
 * @returns A cancelable promise resolving to the list of pending invitations
 */
export function getPendingInvitations(
): CancelablePromise<Array<PendingInvitation>> {
    return __request(OpenAPIHousien, {
        method: 'GET',
        url: '/user/invitations',
    });
}

/**
 * Revokes an outstanding agency invitation via
 * `DELETE /agencies/{agencyId}/invitations/{invitationId}`.
 *
 * @param agencyId - The agency identifier
 * @param invitationId - The invitation to revoke
 * @returns A cancelable promise resolving to the revoke success response
 *   (rejects with 403 when the caller lacks permission)
 */
export function revokeInvitation(
agencyId: string,
invitationId: string,
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'DELETE',
        url: '/agencies/{agencyId}/invitations/{invitationId}',
        path: {
            'agencyId': agencyId,
            'invitationId': invitationId,
        },
        errors: {
            403: `Permission denied`,
        },
    });
}

/**
 * Lists all pending invitations for an agency via
 * `GET /agencies/{agencyId}/invitations`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the array of pending invitations
 */
/**
 * Lists all pending invitations issued by an agency via
 * `GET /agencies/{agencyId}/invitations`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the agency's pending invitations
 */
export function getAgencyInvitations(
agencyId: string,
): CancelablePromise<Array<PendingInvitation>> {
    return __request(OpenAPIHousien, {
        method: 'GET',
        url: '/agencies/{agencyId}/invitations',
        path: {
            'agencyId': agencyId,
        },
    });
}
